(() => {
  if (customElements.get('umg-newsletter-popup')) return;

  const remembered = new Map();
  const sessions = new Set();
  const focusSelector = 'button:not([disabled]),a[href],input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const readFlag = (storage, key, fallback) => {
    try {
      return storage.getItem(key) === 'true' || fallback;
    } catch {
      return fallback;
    }
  };
  const storage = name => {
    try {
      return window[name];
    } catch {
      return null;
    }
  };
  const slug = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.preference = this.querySelector('[data-umg-popup-remember]');
      this.closeButton = this.querySelector('[data-umg-popup-close]');
      this.environment = this.dataset.environment === 'production' ? 'production' : 'staging';
      this.key = `umg:popup:newsletter-signup:${this.environment}`;
      this.sessionKey = `${this.key}:session-closed`;
      this.pendingKey = `${this.key}:submission-pending`;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      if (this.dataset.enabled !== 'true' || !this.dialog || !this.form || !this.routeAllowed()) return;
      if (!this.dialog.showModal) return;
      if (this.preference) this.preference.checked = this.isDismissed();
      this.closeButton?.addEventListener('click', () => this.closePopup(), options);
      this.querySelector('[data-umg-popup-success-close]')?.addEventListener('click', () => this.closePopup(), options);
      this.preference?.addEventListener('change', () => this.rememberChoice(this.preference.checked), options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closePopup();
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closePopup();
      }, options);
      this.dialog.addEventListener('keydown', event => this.keepFocus(event), options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.form.addEventListener('submit', event => this.beforeSubmit(event), options);
      this.querySelector('[data-umg-popup-retry]')?.addEventListener('click', () => this.openPopup(true), options);
      window.addEventListener('storage', event => {
        if (event.key !== this.key) return;
        const dismissed = event.newValue === 'true';
        remembered.set(this.key, dismissed);
        if (this.preference) this.preference.checked = dismissed;
        if (!dismissed) return;
        if (this.dialog.open) this.closePopup();
        this.cancelTimer();
      }, options);
      const stateNode = this.querySelector('[data-umg-popup-form-state]');
      let state = this.form.dataset.serverState || 'idle';
      try { if (stateNode) state = JSON.parse(stateNode.textContent).state; } catch {}
      this.serverState = state;
      const pending = readFlag(storage('sessionStorage'), this.pendingKey, sessions.has(this.pendingKey));
      const ownOutcome = pending || window.location.hash === `#${this.form.id}` || this.dataset.demoOutcome === 'true';
      const errors = this.querySelector('[data-umg-popup-errors]');
      if (errors && this.serverState === 'error' && !ownOutcome) errors.hidden = true;
      if (this.serverState === 'success' && ownOutcome) {
        this.closeForSession();
        this.clearPending();
        this.showSuccess();
        this.updateState('subscribed');
        return;
      }
      if (this.serverState === 'error' && ownOutcome) {
        this.clearPending();
        this.openPopup(true);
        return;
      }
      if (pending) {
        this.closeForSession();
        this.clearPending();
        this.showAnnouncement(this.dataset.unconfirmedMessage || '', true);
        this.updateState('submission-returned');
        return;
      }
      if (this.isDismissed()) {
        this.updateState('remembered');
        return;
      }
      if (this.sessionClosed()) {
        this.updateState('session-closed');
        return;
      }
      this.updateState('ready');
      if (this.dataset.autostart === 'true') this.scheduleOpen();
    }

    routeAllowed() {
      const path = window.location.pathname;
      if (this.dataset.excludeCart !== 'false' && /(^|\/)(cart|checkout)(\/|$)/.test(path)) return false;
      if (this.dataset.excludeAccounts !== 'false' && /(^|\/)(account|customer_authentication|challenge)(\/|$)/.test(path)) return false;
      return this.dataset.tradeCustomer !== 'true';
    }

    isDismissed() {
      return readFlag(storage('localStorage'), this.key, remembered.get(this.key) === true);
    }

    sessionClosed() {
      return readFlag(storage('sessionStorage'), this.sessionKey, sessions.has(this.sessionKey));
    }

    rememberChoice(checked) {
      remembered.set(this.key, checked);
      try {
        const local = storage('localStorage');
        if (checked) local?.setItem(this.key, 'true');
        else local?.removeItem(this.key);
      } catch {}
      this.updateState(checked ? 'remembered' : 'open');
    }

    closeForSession() {
      sessions.add(this.sessionKey);
      try { storage('sessionStorage')?.setItem(this.sessionKey, 'true'); } catch {}
    }

    clearPending() {
      sessions.delete(this.pendingKey);
      try { storage('sessionStorage')?.removeItem(this.pendingKey); } catch {}
    }

    scheduleOpen() {
      const delay = Math.max(0, Math.min(30, Number(this.dataset.delay) || 0)) * 1000;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        if (document.hidden || document.querySelector('dialog[open]')) {
          this.updateState('deferred');
          return;
        }
        this.openPopup();
      }, delay);
    }

    showSuccess() {
      const panel = this.querySelector('[data-umg-popup-success]');
      const heading = this.querySelector('[data-umg-popup-success-title]');
      const signup = this.querySelector('[data-umg-popup-signup]');
      if (!panel || !heading || !signup) {
        this.showAnnouncement(this.dataset.successMessage || '');
        return;
      }
      this.cancelTimer();
      signup.hidden = true;
      panel.hidden = false;
      this.dialog.setAttribute('aria-labelledby', heading.id);
      this.dialog.removeAttribute('aria-describedby');
      this.openPopup(true, heading);
    }

    showAnnouncement(message, allowRetry = false) {
      const announcement = this.querySelector('[data-umg-popup-announcement]');
      if (!announcement) return;
      announcement.hidden = false;
      const text = this.querySelector('[data-umg-popup-announcement-text]');
      if (text) text.textContent = message;
      else announcement.textContent = message;
      const retry = this.querySelector('[data-umg-popup-retry]');
      if (retry) retry.hidden = !allowRetry;
    }

    cancelTimer() {
      if (this.timer != null) window.clearTimeout(this.timer);
      this.timer = null;
    }

    openPopup(showErrors = false, focusTarget = null) {
      if (this.dataset.enabled !== 'true' || !this.dialog || !this.routeAllowed()) return false;
      if (!showErrors && (this.isDismissed() || this.sessionClosed())) {
        this.updateState(this.isDismissed() ? 'remembered' : 'session-closed');
        return false;
      }
      if (this.dialog.open) return true;
      this.previousFocus = document.activeElement;
      this.originalOverflow = document.documentElement.style.overflow;
      this.originalBodyOverflow = document.body.style.overflow;
      this.scrollLocked = true;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      const errors = showErrors ? this.querySelector('[data-umg-popup-errors]') : null;
      const target = focusTarget || errors || this.querySelector('[data-umg-popup-title]');
      const visibleTarget = target?.getClientRects().length ? target : this.querySelector('[data-umg-popup-title]');
      this.updateState(showErrors && errors?.getClientRects().length ? 'error' : 'open');
      (visibleTarget || this.closeButton || this.dialog).focus({ preventScroll: true });
      return true;
    }

    closePopup() {
      this.cancelTimer();
      if (this.preference?.checked) this.rememberChoice(true);
      else this.closeForSession();
      if (this.dialog?.open) this.dialog.close();
      this.updateState(this.isDismissed() ? 'remembered' : 'session-closed');
    }

    afterClose() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      document.documentElement.style.overflow = this.originalOverflow || '';
      document.body.style.overflow = this.originalBodyOverflow || '';
      if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    }

    keepFocus(event) {
      if (event.key !== 'Tab') return;
      const controls = Array.from(this.dialog.querySelectorAll(focusSelector)).filter(node => node.getClientRects().length > 0);
      if (!controls.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }
      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !controls.includes(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !controls.includes(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    updateTags() {
      const tags = ['newsletter', 'discovery_popup'];
      for (const input of this.querySelectorAll('[data-umg-popup-genre]:checked')) {
        const value = slug(input.value);
        if (value) tags.push(`umg-newsletter-genre:${value}`);
      }
      const country = this.querySelector('[data-umg-popup-country]');
      if (country?.value && Array.from(country.options).some(option => option.value === country.value)) tags.push(`umg-newsletter-country:${slug(country.value)}`);
      const hidden = this.querySelector('[data-umg-popup-tags]');
      if (hidden) hidden.value = [...new Set(tags)].join(',');
      return tags;
    }

    beforeSubmit(event) {
      this.updateTags();
      if (this.dataset.demo === 'true') {
        event.preventDefault();
        const status = this.querySelector('[data-umg-popup-demo-status]');
        if (status) status.textContent = 'Preview only. No information is sent or stored.';
        return;
      }
      sessions.add(this.pendingKey);
      try { storage('sessionStorage')?.setItem(this.pendingKey, 'true'); } catch {}
    }

    updateState(state) {
      this.dataset.popupState = state;
      this.dispatchEvent(new CustomEvent('umg:newsletter-popup:state', { bubbles: true, detail: { state } }));
    }

    resetDemo() {
      if (this.dataset.demo !== 'true') return false;
      remembered.delete(this.key);
      sessions.delete(this.sessionKey);
      this.clearPending();
      try { storage('localStorage')?.removeItem(this.key); } catch {}
      try { storage('sessionStorage')?.removeItem(this.sessionKey); } catch {}
      if (this.preference) this.preference.checked = false;
      this.updateState('ready');
      return true;
    }

    disconnectedCallback() {
      this.cancelTimer();
      if (this.dialog?.open) {
        this.dialog.close();
        this.afterClose();
      }
      this.controller?.abort();
      this.initialized = false;
    }
  }

  customElements.define('umg-newsletter-popup', NewsletterPopup);
})();
